"use client";

import { useRef } from "react";
import {
  BrowserWindow,
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import {
  EASE_OUT,
  gsap,
  NOT_REDUCED,
  playOnEnter,
  useGSAP,
} from "@/components/sections/kit/motion";
import { CHANNEL_BARS, KPIS, LINK_DOMAIN, sparkPoints } from "./data";

const SR_SUMMARY =
  "Illustrative analytics view for the campaign's domain, go.acme.com, over the last 30 days: " +
  "3,606 human requests (212 known bots excluded), mobile traffic 71% (2,560 requests), top " +
  "browser Safari, 978 QR scans (27.1% of visits). Campaign tags by utm_source: newsletter 1,284, " +
  "instagram-bio 942, flyer-qr 978, partner 402 — read from the short link each visitor clicked. " +
  "Devices: iOS 44%, Android 27%, desktop 29%, across 14 countries.";

/**
 * Measure — an illustrative mock of the real dashboard analytics page
 * (my.rii.link/analytics), which filters by domain and period (24h / 7d /
 * 30d) — there is no campaign page or per-link filter in the UI; MCP's
 * analytics_summary can narrow to one link. The channel bars and the 30-day
 * sparkline draw themselves on enter.
 */
export default function MeasureSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const spark = rootRef.current?.querySelector<SVGPolylineElement>("[data-spark-line]");
        const tl = gsap
          .timeline({ paused: true, defaults: { ease: EASE_OUT } })
          .from("[data-kpi]", { y: 16, opacity: 0, duration: 0.6, stagger: 0.08 }, 0)
          .from(
            "[data-dash-bar]",
            { scaleX: 0, transformOrigin: "left center", duration: 0.9, stagger: 0.08 },
            0.2,
          );
        if (spark) {
          tl.fromTo(
            spark,
            { strokeDashoffset: 1 },
            { strokeDashoffset: 0, duration: 1.2, ease: "power1.inOut" },
            0.35,
          ).from("[data-spark-fill]", { opacity: 0, duration: 0.5 }, 1.1);
        }
        return playOnEnter(tl, rootRef.current);
      });
    },
    { scope: rootRef },
  );

  return (
    <SectionShell id="measure" className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px">
      <div ref={rootRef} className="flex flex-col">
        <SectionHead
          eyebrow="Measure"
          title="Every channel, one honest scoreboard."
          lead="Each bar is the same link, split by the utm_source on the short link each visitor clicked — a Destination tab beside it counts the campaign tags on the page they were sent to. Illustrative: the dashboard's 30-day view of the campaign's domain — your AI agent can narrow it to one link."
        />

        <div data-grid-block data-snug className="max-md:mt-10">
          <p className="sr-only">{SR_SUMMARY}</p>
          <div aria-hidden="true">
            <BrowserWindow url="my.rii.link/analytics?host=go.acme.com">
              <div className="bg-surface px-[26px] py-[22px] max-sm:px-4">
                {/* header row */}
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-onest text-[17px] font-extrabold tracking-[-0.02em]">
                    Analytics
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] font-jetbrains text-[9.5px] font-bold tracking-[0.1em] text-brand-600 [background:color-mix(in_srgb,var(--color-brand-600)_8%,white)] [border-color:color-mix(in_srgb,var(--color-brand-600)_30%,transparent)]">
                    LAST 30 DAYS
                  </span>
                  <span className="ml-auto font-jetbrains text-[11px] text-dim max-sm:hidden">
                    domain · {LINK_DOMAIN}
                  </span>
                </div>

                {/* KPI row */}
                <div className="mt-[18px] grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {KPIS.map((k) => (
                    <div
                      key={k.label}
                      data-kpi
                      className="rounded-ref-sm border border-line bg-surface-2 px-3.5 py-3"
                    >
                      <div className="font-jetbrains text-[10px] uppercase tracking-[0.1em] text-dim">
                        {k.label}
                      </div>
                      <div className="mt-1 font-onest text-[22px] font-extrabold tracking-[-0.02em]">
                        {k.value}
                      </div>
                      <div
                        className={`font-jetbrains text-[10px] ${
                          k.accent ? "text-data-green" : "text-dim"
                        }`}
                      >
                        {k.sub}
                      </div>
                    </div>
                  ))}
                </div>

                {/* channel bars + sparkline */}
                <div className="mt-3.5 grid gap-3 lg:grid-cols-[7fr_5fr]">
                  <div className="rounded-ref-sm border border-line px-[18px] py-4">
                    <div className="text-[12.5px] font-bold">
                      Campaign tags{" "}
                      <span className="font-jetbrains text-[10px] font-normal text-dim">
                        · short link · source
                      </span>
                    </div>
                    <div className="mt-3 flex flex-col gap-[9px]">
                      {CHANNEL_BARS.map((b) => (
                        <div
                          key={b.name}
                          className="grid grid-cols-[104px_minmax(0,1fr)_46px] items-center gap-3"
                        >
                          <span className="font-jetbrains text-[11px] text-muted">{b.name}</span>
                          <span className="block h-4 overflow-hidden rounded-[4px] bg-surface-2">
                            <span
                              data-dash-bar
                              className="block h-full opacity-85"
                              style={{ width: `${b.w}%`, background: b.color }}
                            />
                          </span>
                          <span className="text-right font-jetbrains text-[11px]">{b.n}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col rounded-ref-sm border border-line px-[18px] py-4">
                    <div className="text-[12.5px] font-bold">Requests over time</div>
                    <svg viewBox="0 0 260 84" className="mt-2.5 w-full">
                      <polyline
                        data-spark-fill
                        points={`0,84 ${sparkPoints()} 260,84`}
                        fill="color-mix(in srgb, var(--color-brand-600) 7%, transparent)"
                        stroke="none"
                      />
                      <polyline
                        data-spark-line
                        points={sparkPoints()}
                        pathLength={1}
                        fill="none"
                        stroke="var(--color-brand-600)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray="1"
                      />
                    </svg>
                    <div className="mt-auto flex gap-3.5 pt-2 font-jetbrains text-[10px] text-dim">
                      <span>iOS 44%</span>
                      <span>Android 27%</span>
                      <span>Desktop 29%</span>
                      <span className="ml-auto">14 countries</span>
                    </div>
                  </div>
                </div>
              </div>
            </BrowserWindow>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
